// src/components/DocumentHistory.tsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, 
  GitCommit, 
  User, 
  Hash,
  Calendar,
  ChevronRight,
  RotateCcw
} from 'lucide-react';
import { GitCommit as GitCommitType } from '../App';
import { format, formatDistanceToNow } from 'date-fns';
import './styles.css';

interface DocumentHistoryProps {
  history: GitCommitType[];
  selectedCommit: GitCommitType | null;
  onSelectCommit: (commit: GitCommitType) => void;
  onReturnToCurrent: () => void;
}

const DocumentHistory: React.FC<DocumentHistoryProps> = ({
  history,
  selectedCommit,
  onSelectCommit,
  onReturnToCurrent
}) => {
  const [expandedCommit, setExpandedCommit] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'timeline'>('list');

  // Group commits by date
  const groupedHistory = history.reduce((groups, commit) => {
    const date = format(new Date(commit.date), 'yyyy-MM-dd');
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(commit);
    return groups;
  }, {} as Record<string, GitCommitType[]>);

  // Toggle commit expansion
  const toggleExpanded = (hash: string) => {
    setExpandedCommit(expandedCommit === hash ? null : hash);
  };

  return (
    <div className="document-history">
      {/* History Header */}
      <div className="history-header">
        <div className="history-title">
          <Clock size={18} />
          <h3>Version History</h3>
        </div>
        
        {selectedCommit && (
          <button 
            onClick={onReturnToCurrent}
            className="return-btn"
            title="Return to current version"
          >
            <RotateCcw size={16} />
          </button>
        )}
      </div>

      {/* View Mode Toggle */}
      <div className="view-mode-toggle">
        <button
          className={`mode-btn ${viewMode === 'list' ? 'active' : ''}`}
          onClick={() => setViewMode('list')}
        >
          List View
        </button>
        <button
          className={`mode-btn ${viewMode === 'timeline' ? 'active' : ''}`}
          onClick={() => setViewMode('timeline')}
        >
          Timeline
        </button>
      </div>

      {/* History Content */}
      <div className="history-content">
        {history.length === 0 ? (
          <div className="no-history">
            <GitCommit size={32} />
            <p>No version history available</p>
            <small>Commit changes to create history</small>
          </div>
        ) : viewMode === 'list' ? (
          // List View
          <div className="history-list">
            <AnimatePresence>
              {Object.entries(groupedHistory).map(([date, commits]) => (
                <motion.div
                  key={date}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="history-group"
                >
                  <div className="group-date">
                    <Calendar size={14} />
                    {format(new Date(date), 'MMMM d, yyyy')}
                  </div>
                  
                  {commits.map((commit) => (
                    <motion.div
                      key={commit.hash}
                      className={`commit-item ${
                        selectedCommit?.hash === commit.hash ? 'selected' : ''
                      }`}
                      whileHover={{ x: 5 }}
                      onClick={() => onSelectCommit(commit)}
                    >
                      <div className="commit-indicator">
                        <div className="commit-line" />
                        <div className="commit-dot" />
                      </div>
                      
                      <div className="commit-content">
                        <div className="commit-main">
                          <div className="commit-message">
                            <GitCommit size={14} />
                            {commit.message}
                          </div>
                          
                          <button
                            className="expand-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpanded(commit.hash);
                            }}
                          >
                            <ChevronRight
                              size={14}
                              className={expandedCommit === commit.hash ? 'expanded' : ''}
                            />
                          </button>
                        </div>
                        
                        <div className="commit-meta">
                          <span className="commit-time">
                            {formatDistanceToNow(new Date(commit.date), { 
                              addSuffix: true 
                            })}
                          </span>
                          <span className="separator">•</span>
                          <span className="commit-author">
                            <User size={12} />
                            {commit.author}
                          </span>
                        </div>
                        
                        <AnimatePresence>
                          {expandedCommit === commit.hash && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="commit-details"
                            >
                              <div className="detail-item">
                                <Hash size={12} />
                                <span className="detail-label">Commit:</span>
                                <code className="commit-hash">
                                  {commit.hash.substring(0, 7)}
                                </code>
                              </div>
                              
                              <div className="detail-item">
                                <Calendar size={12} />
                                <span className="detail-label">Date:</span>
                                <span>{format(new Date(commit.date), 'PPpp')}</span>
                              </div>
                              
                              <div className="detail-item">
                                <User size={12} />
                                <span className="detail-label">Author:</span>
                                <span>{commit.email}</span>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          // Timeline View
          <div className="history-timeline">
            <div className="timeline-track">
              {history.map((commit, index) => {
                const isSelected = selectedCommit?.hash === commit.hash;
                const date = new Date(commit.date);
                
                return (
                  <motion.div
                    key={commit.hash}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className={`timeline-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => onSelectCommit(commit)}
                    style={{
                      top: `${index * 80}px`
                    }}
                  >
                    <div className="timeline-marker">
                      <div className="timeline-line" />
                      <div className="timeline-dot" />
                    </div>
                    
                    <div className="timeline-content">
                      <div className="timeline-date">
                        {format(date, 'MMM d, yyyy HH:mm')}
                      </div>
                      <div className="timeline-message">
                        {commit.message}
                      </div>
                      <div className="timeline-author">
                        {commit.author}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* History Footer */}
      <div className="history-footer">
        <span className="history-count">
          {history.length} version{history.length !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
};

export default DocumentHistory;